// `external-source-filters.tpl.html` + `external-source-filters-editor.tpl.html` — the criteria
// builder's ninth and last section.
//
// ⚠️ IT IS ONE TABLE PER SOURCE, NOT ONE TABLE. The section repeats over every configured
// external source, each with its own logo, name and independent filter list — so the criteria
// it produces is a MAP keyed by source, not an array. The ATS section next to it is a single
// table, and the two are otherwise drawn identically, which is exactly what makes the shape
// easy to get wrong.
//
// ⚠️ AND THE PICKER IS COMMENTED OUT UPSTREAM. The template keeps ten lines of an
// `<external-source-picker>` / single-editor arrangement behind `<!-- -->`, above the repeat
// that actually renders. Porting what is commented out would build a source switcher production
// does not show.
import { createElement, type ReactElement } from 'react'
import type { ExternalSourceCriteriaFilterConditionType } from '@woo-ext/api'
import { TagsInput } from '../common/TagsInput.tsx'
import {
  EXTERNAL_SOURCE_CONDITION_TEXT,
  EXTERNAL_SOURCE_FILTER_HEADLINES,
  externalSourceFieldFor,
  isExternalSourceFilterValid,
  newExternalSourceFilterRow,
  type ExternalSourceFilterField,
  type ExternalSourceFilterRow,
  type ExternalSourceFilterValue,
} from './externalSourceFilterData.ts'

export interface ExternalSourceGroup {
  hostSite: string
  /** `SocialHostDisplayName[hostSite]` — the label beside the logo. */
  displayName: string
  /** `iconsMap[hostSite]` — a CDN path upstream. */
  iconUrl?: string
  fields: readonly ExternalSourceFilterField[]
}

export interface ExternalSourceFiltersSectionProps {
  sources: readonly ExternalSourceGroup[]
  /** `tempExternalSourceSearchCriteriaFilters` — rows PER SOURCE, keyed by `hostSite`. */
  rowsBySource?: Readonly<Record<string, readonly ExternalSourceFilterRow[]>>
  showErrors?: boolean
  onChange?: (hostSite: string, rows: ExternalSourceFilterRow[]) => void
}

const NEEDS_INPUT = 'Heads up! This field needs your input'

function ValueCells({
  index,
  row,
  field,
  invalid,
  onRow,
}: {
  index: number
  row: ExternalSourceFilterRow
  field: ExternalSourceFilterField
  invalid: boolean
  onRow: (row: ExternalSourceFilterRow) => void
}): ReactElement | null {
  const value = row.value
  if (value === undefined) return null

  const onValue = (next: ExternalSourceFilterValue): void => onRow({ ...row, value: next })

  // ⚠️ THE OPTIONS ARE THE FIELD'S, AND THE TEXT COMES FROM A MAP. Three conditions exist;
  // a given field may allow one of them.
  const conditionSelect = (name: string): ReactElement => (
    <div className="external-source-filters-condition criteria-cell">
      <div className="select">
        <select
          className="form-control input"
          name={`${name}${String(index)}`}
          required
          value={row.conditionType ?? ''}
          onChange={(event) =>
            onRow({ ...row, conditionType: event.target.value as ExternalSourceCriteriaFilterConditionType })
          }
        >
          {field.supportedConditionTypes.map((condition) => (
            <option key={condition} value={condition}>
              {EXTERNAL_SOURCE_CONDITION_TEXT[condition]}
            </option>
          ))}
        </select>
      </div>
    </div>
  )

  switch (value.type) {
    case 'TEXT':
      return (
        <>
          {conditionSelect('externalSourceFiltersTextCondition')}
          <div className="external-source-filters-text-input criteria-cell">
            <div>
              <TagsInput
                className={`tags-input-default-width${invalid ? ' external-source-filters-tags-field-error' : ''}`}
                name={`externalSourceFiltersText${String(index)}`}
                placeholder="Add values"
                tags={value.values.map((text) => ({ text }))}
                freeText
                onAdd={(tag) => onValue({ ...value, values: [...value.values, tag.text] })}
                onRemove={(at) => onValue({ ...value, values: value.values.filter((_, i) => i !== at) })}
              />
            </div>
            {invalid ? <label className="control-label has-error">{NEEDS_INPUT}</label> : null}
          </div>
        </>
      )

    case 'MULTI_SELECT':
      return (
        <>
          {conditionSelect('externalSourceFiltersMultiselectCondition')}
          <div className="external-source-filters-text-input criteria-cell">
            <div>
              {/* Autocomplete-only, against the FIELD's `supportedValues` — a fixed list, not a
                  query. The tag's text is the value's `displayName` and the criterion stores
                  its `value`. */}
              <TagsInput
                className={`tags-input-default-width${invalid ? ' external-source-filters-tags-field-error' : ''}`}
                name={`externalSourceFiltersMultiselect${String(index)}`}
                placeholder="Add values"
                tags={value.values.map((stored) => ({
                  text: field.supportedValues.find((option) => option.value === stored)?.displayName ?? stored,
                }))}
                suggestions={field.supportedValues
                  .filter((option) => !value.values.includes(option.value))
                  .map((option) => ({ text: option.displayName }))}
                onAdd={(tag) => {
                  const picked = field.supportedValues.find((option) => option.displayName === tag.text)
                  if (picked === undefined) return
                  onValue({ ...value, values: [...value.values, picked.value] })
                }}
                onRemove={(at) => onValue({ ...value, values: value.values.filter((_, i) => i !== at) })}
              />
            </div>
            {invalid ? <label className="control-label has-error">{NEEDS_INPUT}</label> : null}
          </div>
        </>
      )

    case 'SINGLE_SELECT':
      return (
        <>
          {conditionSelect('externalSourceFiltersSingleselectCondition')}
          <div className="external-source-filters-text-input criteria-cell">
            <div className="select">
              <select
                className={`form-control input${invalid ? ' external-source-filters-select-field-error' : ''}`}
                name={`externalSourceFiltersSingleselect${String(index)}`}
                value={value.value ?? ''}
                onChange={(event) => onValue({ ...value, value: event.target.value })}
              >
                {field.supportedValues.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.displayName}
                  </option>
                ))}
              </select>
            </div>
            {invalid ? <label className="control-label has-error">{NEEDS_INPUT}</label> : null}
          </div>
        </>
      )

    case 'NUMERIC':
      return (
        <>
          <div className="external-source-filters-number-value criteria-cell">
            <input
              name={`externalSourceFiltersMinYears${String(index)}`}
              className={`form-control input${value.min === null && invalid ? ' external-source-filters-input-field-error' : ''}`}
              type="number"
              min={0}
              required
              value={value.min ?? ''}
              onChange={(event) =>
                onValue({ ...value, min: event.target.value === '' ? null : Number(event.target.value) })
              }
            />
          </div>
          <div className="external-source-filters-number-value criteria-cell">
            <input
              name={`externalSourceFiltersMaxYears${String(index)}`}
              className={`form-control input${value.max === null && invalid ? ' external-source-filters-input-field-error' : ''}`}
              type="number"
              min={value.min ?? 0}
              required
              value={value.max ?? ''}
              onChange={(event) =>
                onValue({ ...value, max: event.target.value === '' ? null : Number(event.target.value) })
              }
            />
            {invalid ? (
              <label className="control-label has-error">
                {value.min !== null && value.max !== null && value.max < value.min
                  ? "'To' must be greater than 'From'"
                  : 'Heads up! Some fields need your input'}
              </label>
            ) : null}
          </div>
        </>
      )

    case 'DATE':
      return (
        <>
          <div className="external-source-filters-date-value criteria-cell">
            <input
              className="form-control input"
              placeholder="DD-MM-YYYY"
              value={formatDate(value.from)}
              onChange={(event) => onValue({ ...value, from: parseDate(event.target.value) })}
            />
          </div>
          <div className="external-source-filters-date-value criteria-cell">
            <input
              className={`form-control input${invalid ? ' external-source-filters-input-field-error' : ''}`}
              placeholder="DD-MM-YYYY"
              required
              value={formatDate(value.to)}
              onChange={(event) => onValue({ ...value, to: parseDate(event.target.value) })}
            />
            {invalid ? (
              <label className="control-label has-error">
                {value.to !== null && value.from !== null && value.to > value.from
                  ? NEEDS_INPUT
                  : 'From date must be earlier than To date'}
              </label>
            ) : null}
          </div>
        </>
      )
  }
}

function formatDate(date: Date | null): string {
  if (date === null) return ''
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${String(date.getFullYear())}`
}

function parseDate(text: string): Date | null {
  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(text)
  if (match === null) return null
  return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]))
}

export function ExternalSourceFiltersSection({
  sources,
  rowsBySource = {},
  showErrors = false,
  onChange,
}: ExternalSourceFiltersSectionProps): ReactElement {
  return (
    <div className="external-source-filters">
      {sources.map((source) => {
        const rows = rowsBySource[source.hostSite] ?? []
        const update = (next: ExternalSourceFilterRow[]): void => onChange?.(source.hostSite, next)
        // ⚠️ ORDERED BY `displayName` HERE, where the ATS table orders by `name`. The two
        // tables look identical and sort on different keys.
        const fields = [...source.fields].sort((a, b) => a.displayName.localeCompare(b.displayName))

        return (
          <div className="external-source-wrap" key={source.hostSite}>
            <label className="bold-soft">
              {/* `icon-img-{{hostSite}}` is a per-source class the stylesheet sizes.

                  ⚠️ THE `src` IS OMITTED WHEN THERE IS NO LOGO, NOT SET TO ''. Upstream writes
                  `cdn-src="{{iconsMap[host]}}"`, and an empty interpolation leaves the
                  directive with nothing to set — no attribute. `src=""` in React resolves to
                  the PAGE's URL, fetches the HTML document as an image and draws a
                  broken-image box, which made each source's label 45px taller than
                  production. Three of the six hosts have no logo. */}
              <img
                className={`icon-img icon-img-${source.hostSite}`}
                {...(source.iconUrl === undefined ? {} : { src: source.iconUrl })}
                alt=""
              />{' '}
              {source.displayName}
            </label>
            {/* ⚠️ `<external-source-filters-editor>` IS AN ELEMENT WITH ITS OWN RULE —
                `display: block; margin-top: 10px` in `_external-source-filters.scss`. The
                class of nearly the same name is the component's template root INSIDE it, and
                the two carry different declarations. Sixth time in this port. */}
            {createElement(
              'external-source-filters-editor',
              null,
              <div className="external-source-filters-editor">
                <div className="external-source-filters-editor-criteria criteria-table">
                  {rows.map((row, index) => {
                    const field = externalSourceFieldFor(source.fields, row.fieldName)
                    const headlines = field === undefined ? [] : EXTERNAL_SOURCE_FILTER_HEADLINES[field.fieldType]
                    const invalid =
                      showErrors &&
                      row.fieldName !== undefined &&
                      row.fieldName !== '' &&
                      !isExternalSourceFilterValid(row)
                    return (
                      // eslint-disable-next-line react/no-array-index-key -- `track by $index` upstream.
                      <div className="external-source-filters-row-group sub-section" key={index}>
                        <div className="criteria-row">
                          <div className="criteria-cell-label">
                            <label className="bold-soft">Field</label>
                          </div>
                          {headlines.map((label) => (
                            <div className="criteria-cell-label" key={label}>
                              <label className="bold-soft">{label}</label>
                            </div>
                          ))}
                        </div>
                        <div className="criteria-row">
                          <div className="external-source-filters-field criteria-cell">
                            <div className="select">
                              <select
                                className="form-control input"
                                value={row.fieldName ?? ''}
                                onChange={(event) => {
                                  const picked = externalSourceFieldFor(source.fields, event.target.value)
                                  update(
                                    rows.map((existing, at) =>
                                      at === index
                                        ? picked === undefined
                                          ? { fieldName: event.target.value }
                                          : newExternalSourceFilterRow(picked)
                                        : existing,
                                    ),
                                  )
                                }}
                              >
                                <option className="color-graylight" value="" disabled>
                                  Please select...
                                </option>
                                {fields.map((option) => (
                                  <option key={option.fieldName} value={option.fieldName}>
                                    {option.displayName}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {showErrors && (row.fieldName === undefined || row.fieldName === '') ? (
                              <label className="control-label has-error">Please pick a field</label>
                            ) : null}
                          </div>
                          {field === undefined ? null : (
                            <ValueCells
                              index={index}
                              row={row}
                              field={field}
                              invalid={invalid}
                              onRow={(next) => update(rows.map((existing, at) => (at === index ? next : existing)))}
                            />
                          )}
                          <div
                            className="remove-criteria-container criteria-cell"
                            role="button"
                            aria-label="Remove"
                            tabIndex={0}
                            onClick={() => update(rows.filter((_, at) => at !== index))}
                            onKeyDown={(event) => {
                              if (event.key !== 'Enter' && event.key !== ' ') return
                              event.preventDefault()
                              update(rows.filter((_, at) => at !== index))
                            }}
                          >
                            <div className="fa-stack fa-1x icons-container">
                              <i className="fas fa-circle fa-stack-1x x-icon-container fa-lg" />
                              <i className="fal fa-times fa-stack-1x cursor-pointer fa-xs" />
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}

                  <div className="criteria-row bottom-cta-row sub-section">
                    <div className="criteria-cell placeholder-cell external-source-filters-first-placeholder" />
                    <div className="criteria-cell placeholder-cell external-source-filters-second-placeholder" />
                    <div className="external-source-filters-add-filter criteria-cell">
                      <a
                        href=""
                        className="add-button"
                        onClick={(event) => {
                          event.preventDefault()
                          update([...rows, {}])
                        }}
                      >
                        <i className="fal fa-plus-circle" />
                        <span>&nbsp;Add</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>,
            )}
          </div>
        )
      })}
    </div>
  )
}
